import React, { useEffect, useState } from "react";
import { Paper, IconButton, Chip, Avatar, Tooltip } from "@mui/material";
import MUIDataTable from "../../../components/LegacyMuiDataTable";
import EditIcon from "@mui/icons-material/Edit";
import Delete from "@mui/icons-material/Delete";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import RemoveIcon from "@mui/icons-material/Remove";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useHistory } from "react-router-dom";

import NavBar from "../../../components/Navbar";
import { getHR, deleteHR } from "../../../store/ducks/duckHr";
import CustomToolbar from "../../../components/CustomToolbar";
import { BorderLinearProgress } from "../../../components/LoadingComponent";
import ConfirmDelete from "../../../components/confirmDelete";

import ManagementHrForm from "./components/ManagementHrForm";

const initialHr = {
  id: 0,
  name: "",
  FiscalYear: "",
};

const ManagementHr = () => {
  const data = useSelector((state) => state.hr.data);
  const loading = useSelector((state) => state.hr.isLoading);
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(0);
  const [name, setName] = useState("");
  const [isEdit, setEdit] = useState(false);
  const history = useHistory();

  const [hr, setHr] = useState(initialHr);

  const [openConfirm, setOpenConfirm] = useState(false);

  const getHrName = (id) => data.find((x) => x.id === id)?.name ?? "";

  useEffect(() => {
    dispatch(getHR());
  }, [dispatch]);

  const handleClickOpen =
    (hr, isEdit = true) =>
    (event) => {
      event.preventDefault();

      setId(hr?.id || 0);
      setName(hr?.name || "");
      setHr(hr);
      setEdit(isEdit);

      setOpen(true);
    };

  const handleClickReload = () => {
    dispatch(getHR());
  };

  const handleClose = () => {
    setHr(initialHr);
    setOpen(false);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleConfirm = (hr) => (event) => {
    event.preventDefault();
    setId(hr?.id || 0);
    setName(hr?.name || "");

    setOpenConfirm(true);
  };

  const handleDelete = (event) => {
    event.preventDefault();
    dispatch(deleteHR(id)).then(() =>
      enqueueSnackbar(`HR Document ${name} Deleted succesfully`, { variant: "info" }),
    );
    handleCloseConfirm();
  };

  const handleRedirect = (hrId) => (e) => {
    e.preventDefault();

    if (hrId) {
      history.push(`/Administration/Hrinfo/${hrId}`);
    }
  };

  const columns = [
    {
      name: "id",
      label: "Id",
      options: {
        filter: true,
        display: false,
        customFilterListOptions: {
          render: (v) => getHrName(v),
        },
        filterOptions: {
          renderValue: (v) => getHrName(v),
        },
      },
    },
    {
      name: "name",
      label: "Name",
      options: {
        filter: true,
        setCellHeaderProps: () => ({
          style: {
            whiteSpace: "nowrap",
            position: "sticky",
            left: 0,
            background: "white",
            zIndex: 101,
          },
        }),
      },
    },
    {
      name: "fiscalYear",
      label: "Fiscal Year",
      options: {
        filter: true,
        setCellHeaderProps: () => ({
          style: {
            whiteSpace: "nowrap",
            position: "sticky",
            left: 0,
            background: "white",
            zIndex: 101,
          },
        }),
      },
    },
    {
      label: "Info",
      name: "info",
      options: {
        filter: true,
        customBodyRenderLite: (dataIndex) => {
          let rowData = data[dataIndex];

          return rowData.info.length > 0 ? (
            rowData.info.map((x) => {
              let data = `${x.url}`;

              return (
                <Chip
                  key={`chip-InfoHR-${x}`}
                  avatar={<Avatar>{x.monthNumber}</Avatar>}
                  color="primary"
                  label={data}
                  size="small"
                  variant="outlined"
                />
              );
            })
          ) : (
            <RemoveIcon />
          );
        },
      },
    },
    {
      name: "",
      label: "",
      options: {
        filter: false,
        sort: false,
        empty: true,
        viewColumns: false,
        setCellHeaderProps: () => ({
          style: {
            whiteSpace: "nowrap",
            position: "sticky",
            left: 0,
            background: "white",
            zIndex: 101,
          },
        }),
        customBodyRenderLite: (dataIndex) => {
          let rowData = data[dataIndex];

          return (
            <div style={{ maxWidth: 250 }}>
              <Tooltip title={"Edit"}>
                <IconButton aria-label="edit" size="small" onClick={handleClickOpen(rowData, true)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={rowData.deleted ? "Is already removed" : "Delete"}>
                <IconButton
                  aria-label="delete"
                  disabled={rowData.deleted}
                  size="small"
                  onClick={handleConfirm(rowData)}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
              <Tooltip title={"Edit HRInfo"}>
                <IconButton aria-label="open" size="small" onClick={handleRedirect(rowData.id)}>
                  <OpenInNewIcon />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
        setCellProps: () => ({ style: { width: 130 } }),
      },
    },
  ];

  const options = {
    filterType: "dropdown",
    expandableRowsHeader: false,
    selectableRows: "none",
    download: false,
    print: false,
    viewColumns: true,
    customToolbar: () => {
      return <CustomToolbar handleClick={handleClickOpen(hr)} handleReload={handleClickReload} />;
    },
    setTableProps: () => {
      return {
        padding: "default",
        size: "small",
      };
    },
    textLabels: {
      body: {
        noMatch: loading ? "loading..." : "Sorry, no matching records found",
      },
    },
    responsive: "standard",
    fixedHeader: true,
    fixedSelectColumn: false,
    tableBodyHeight: "420px",
    jumpToPage: true,
  };

  return (
    <>
      <NavBar />
      <Paper
        style={{
          marginTop: 100,
        }}
      >
        {loading && <BorderLinearProgress />}

        <ConfirmDelete
          handleClose={handleCloseConfirm}
          handleDelete={handleDelete}
          message={`Are you sure to Delete the HR Document with Name: ${name}?`}
          open={openConfirm}
        />
        {open && hr && (
          <ManagementHrForm
            handleClose={handleClose}
            initialValues={hr}
            isEdit={isEdit}
            open={open}
          />
        )}
        <MUIDataTable columns={columns} data={data} options={options} title={"HR"} />
      </Paper>
    </>
  );
};

export default ManagementHr;
